import React from 'react';
import { PaylinkResponse } from 'skipify-types';
import { Box, colors, Stack, Typography, Collapse, IconButton } from '@mui/material';
import dayjs from 'dayjs';

import { ExpandMore } from '@mui/icons-material';
import { DeliveryMethods, PaylinkDetails, PaylinkStatusChip } from '../components';
import { getDisplayedPaylinkAmount, getDisplayedPaylinkUserInfo } from '../../../util/helpers';

interface Props {
  paylink: PaylinkResponse;
  setIsExpanded: (paylinkId: string | null) => void;
  isExpanded: boolean;
  merchantName: string;
  merchantId: string;
}

const RecentPaylink = ({
  paylink,
  isExpanded,
  setIsExpanded,
  merchantName,
  merchantId,
}: Props): JSX.Element => {
  const { status, sendEmail, sendSms, lastSentAt } = paylink;

  return (
    <Box sx={{ position: 'relative' }}>
      <Stack
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: `1px solid ${colors.grey[300]}`,
          height: isExpanded ? '684px' : '115px',
          transition: 'height 0.5s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            pt: 3,
            px: 3,
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center">
            <PaylinkStatusChip status={status} />
            <Typography sx={{ fontSize: 12, color: 'grey.600' }}>
              {dayjs(lastSentAt).format('M/D/YY h:mma')}
            </Typography>
          </Stack>
          <IconButton
            aria-label="View Paylink"
            onClick={() => setIsExpanded(isExpanded ? null : paylink.paylinkId)}
            sx={{
              color: 'grey.800',
              marginTop: -1.5,
              marginRight: -2,
              transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMore />
          </IconButton>
        </Box>
        <Collapse in={isExpanded} mountOnEnter>
          <Box sx={{ height: '632px', overflowY: 'auto', overflowX: 'hidden' }}>
            <PaylinkDetails merchantName={merchantName} paylink={paylink} merchantId={merchantId} />
          </Box>
        </Collapse>
        <Collapse in={!isExpanded}>
          <Box
            sx={{
              display: 'grid',
              columnGap: 2,
              gridTemplateColumns: '2fr 1fr 1fr',
              '& > *:last-child': {
                justifySelf: 'end',
              },
              alignItems: 'center',
              '& p': {
                color: 'grey.900',
                fontSize: '14px',
                textWrap: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              },
              pt: 2,
              px: 3,
              pb: 3,
            }}
          >
            <Typography>{getDisplayedPaylinkUserInfo(paylink).userInfo}</Typography>
            <Typography>{getDisplayedPaylinkAmount(paylink)}</Typography>
            <DeliveryMethods sendEmail={sendEmail} sendSms={sendSms} />
          </Box>
        </Collapse>
      </Stack>
    </Box>
  );
};

export default RecentPaylink;
